import {
  errorClassificationLookup,
  policyType,
  policyTypeLines,
  rejectionTypeToPolicyTypeMap
} from '@ourbranch/lookups';
import { appConfig } from 'brand-config';

import { autoDetailsCodes, addAutoCodes } from './quote-forms.constants';

// Whether the selected policy type writes an auto line at all (A, AR, HA, CA, FA, AB).
export const policyTypeIncludesAuto = (selectedPolicyType) =>
  Boolean(policyTypeLines[selectedPolicyType]?.includes(policyType.Auto));

/**
 * Whether "Current Auto Details" renders from the start rather than waiting to be revealed.
 *
 * By default the section is progressive-disclosure: it stays hidden until the rater's missing-data
 * cascade asks for it (code 5003), and that same alert then makes its two fields client-side
 * required. On a policy type that already writes an auto line the answer is never in doubt, and the
 * default produced a loop agents could not get out of (#35337) — See Price, get 5003, miss the
 * section that just appeared further down the page, hit Resubmit, and get the generic "complete the
 * highlighted fields" toast pointing at fields they never saw.
 *
 * On for the brands whose agents quote auto directly (CFM); off for Branch, whose auto quotes lean
 * on the data pull and deliberately don't ask up front.
 */
export const showsCurrentAutoDetailsUpFront = (selectedPolicyType) =>
  Boolean(appConfig?.featureFlags?.showCurrentAutoDetailsUpFront) && policyTypeIncludesAuto(selectedPolicyType);

/**
 * Whether the "Add Car" (VIN) section renders from the start rather than waiting to be revealed.
 *
 * Like Current Auto Details, the vehicle-entry section is progressive-disclosure by default: it
 * stays hidden until the rater asks for a VIN (codes 5005/50001, addAutoCodes). But a monoline auto
 * with no VIN entered and a current-coverage (Verisk CV) no-hit yields no discovered vehicle, so the
 * rater returns an offer with zero priced options and — because NO_CARS is a bypass-level rejection
 * that just drops the auto line — no VIN-needed alert. The agent is stranded at See Price with no
 * way to add a vehicle (#35337). Rendering the VIN field up front on a policy type that writes an
 * auto line lets the agent enter a vehicle directly, the same way CFM already quotes auto directly.
 *
 * On for CFM; off for Branch, whose auto quotes lean on the data pull to discover vehicles.
 */
export const showsAddVehicleUpFront = (selectedPolicyType) =>
  Boolean(appConfig?.featureFlags?.showAddVehicleUpFront) && policyTypeIncludesAuto(selectedPolicyType);

/**
 * Sections that stay open — and therefore keep their values through handleCleanUp — for this policy
 * type regardless of what the rater has asked for. A section that is visible only because it was
 * pinned must also be pinned here, or the agent's answers are wiped on the first round trip
 * (cleanAutoDetailsForm / cleanAddCarForm blank fields for any auto section not kept).
 */
export const pinnedSectionsForPolicyType = (selectedPolicyType) => [
  ...(showsCurrentAutoDetailsUpFront(selectedPolicyType) ? autoDetailsCodes : []),
  ...(showsAddVehicleUpFront(selectedPolicyType) ? addAutoCodes : [])
];

/**
 * What the new-quote form starts from. URL params win over an offer being re-quoted; a URL affinity
 * wins over the agent's active Dialpad screenpop, which only applies when nothing else set one.
 */
export function buildQuotePrefill({ offerQuote, prefillData, params, activeScreenPop }) {
  const prefill = {
    ...offerQuote,
    ...prefillData,
    ...params,
    affinity: params.affinity?.replaceAll(' ', '') || activeScreenPop?.affinity
  };
  if (!params.leadSource && activeScreenPop?.leadSource) {
    prefill.leadSource = activeScreenPop.leadSource;
  }
  return prefill;
}

/** `+12292213376` → `(229) 221-3376`; anything that isn't a US number is returned as-is. */
export function formatCallerPhone(phone) {
  const digits = (phone ?? '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  return digits.length === 10 ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}` : phone;
}

// BYPASS_ALERT and DISMISSIBLE_REJECTION only block navigation when relevant to the selected policy type.
// Other alert types (plain ALERT) always block
export function shouldAlertBlockNavigation(alert, policyType) {
  const alertType = errorClassificationLookup[Number(alert.code)];
  if (alertType === 'BYPASS_ALERT' || alertType === 'DISMISSIBLE_REJECTION') {
    return rejectionTypeToPolicyTypeMap[alert.type]?.includes(policyType) ?? false;
  }
  return true;
}

// The rater's missing-data cascade returns one code per round trip (5001 → 5002 → 5003 → ...).
// Map each code to the input that resolves it so the agent lands on the exact field instead of
// hunting through the revealed section. Omitted on purpose: 5001 (prior-address inputs hide
// behind the lived-here-1-year radio) and 5010 (the VIN is already filled and often correct —
// 5010 also fires on vendor failures, so focusing the field nudges agents to retype a good VIN;
// see #33196).
export const ALERT_FIELD_FOCUS_MAP = {
  5002: 'dateOfBirth',
  5003: 'policyLimitBIPD',
  5005: 'VIN',
  5006: 'VIN',
  5012: 'driversLicenseNumber',
  5015: 'driversLicenseState',
  5016: 'dateOfBirth',
  5023: 'home.latitude',
  5025: 'unit',
  50001: 'VIN'
};

// Section render happens after setSectionsToShow, so defer the DOM focus a tick (same pattern
// as ScrollToError's mount scroll). preventScroll leaves the section's own alert scroll as the
// visual anchor; marking the field touched makes its alert-schema "required" message render
// under the exact input immediately instead of on the next failed submit.
export function focusFirstAlertField(alerts, setFieldTouched) {
  const fieldId = alerts?.map((alert) => ALERT_FIELD_FOCUS_MAP[Number(alert.code)]).find(Boolean);
  if (!fieldId) {
    return;
  }
  setFieldTouched?.(fieldId, true, true);
  setTimeout(() => {
    document.getElementById(fieldId)?.focus({ preventScroll: true });
  }, 150);
}
